import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { SmallHeadline } from 'components/Typography'
import { Tabs } from 'components/Tab'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { BlueButton, NavButton, ScrollTopButton } from 'components/Button'
import { fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { getStoreRef } from 'reducks/auth/selectors'
import { OrderBreadList } from 'components/List'
import { getOrders } from 'reducks/checkout/selectors'
import { NoOrderSection } from 'components/Order'
import { fetchBreads } from 'reducks/bread/operations'
import { fetchDailyStocks } from 'reducks/stock/operations'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { startOfMonth } from 'date-fns'
import { FlexRow } from 'components/Flex'
import { push } from 'connected-react-router'
import { useOrdersByBread } from 'hooks/order'
import { useFlatCheckoutItems } from 'hooks/checkout'
import { fetchSchedules } from 'reducks/schedule/operations'

type Props = {
  isPast: boolean
}

const OrderByBreadTemplate: FC<Props> = ({ isPast }) => {
  const dispatch = useDispatch()
  const orders = useSelector((state: State) => getOrders(state))
  const pathname = useSelector((state: State) => state.router.location.pathname)
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const date = /\/order\/[0-9]{8}\/product/.test(pathname)
    ? pathname.split('/order/')[1].split('/product')[0]
    : undefined
  const items = useFlatCheckoutItems(orders)
  const ordersByBread = useOrdersByBread(items)

  const [expandedBreadIds, setExpandedBreadIds] = useState<string[]>([])

  const handleExpandedBreadIds = useCallback(
    (id: string) => {
      setExpandedBreadIds((prevState) => {
        if (prevState.includes(id)) {
          return prevState.filter((_id) => _id !== id)
        } else {
          return [...prevState, id]
        }
      })
    },
    [setExpandedBreadIds]
  )

  const printOrderByBread = useCallback(() => {
    dispatch(push(`/order/print/${date}/product?expandedBreadIds=${expandedBreadIds}`))
  }, [date, expandedBreadIds])

  // 日付が変わったら受取番号と注文を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initOrders())
    dispatch(fetchOrders(date, 'すべて', ['daily', 'preorder'], ['succeeded', 'requires_capture'], storeRef))
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [date, storeRef])

  // 日付が変わったら受取番号と注文を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(fetchDailyStocks(date, storeRef))
  }, [date, storeRef])

  // 受付設定を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', 'name'))
  }, [storeRef])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchSchedules(storeRef, ['daily', 'preorder']))
  }, [storeRef])

  // 臨時休業日・営業日を取得（注文が1件もない場合の一斉通知のために使用する）
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  const tabs = [
    { isSelected: false, label: '受取番号別', url: pathname.split('/product')[0] },
    { isSelected: true, label: '商品別', url: pathname },
  ]

  return (
    <div className={styles.standard}>
      <Tabs tabs={tabs} />
      <Spacer size={73} />
      <FlexRow justifyContent={'end'}>
        <BlueButton label={'印刷する'} onClick={printOrderByBread} size={'xsmall'} type={'main'} />
      </FlexRow>
      <Spacer size={20} />
      <SmallHeadline label={`${ordersByBread.length}件の商品を表示中`} />
      <Spacer size={10} />
      {ordersByBread.length > 0 ? (
        <OrderBreadList handleExpandedBreadIds={handleExpandedBreadIds} orderBreads={ordersByBread} />
      ) : (
        <NoOrderSection isPast={isPast} />
      )}
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
      <ScrollTopButton />
      <Spacer size={20} />
    </div>
  )
}

export default OrderByBreadTemplate
